import React, { useRef } from 'react';
import { toast } from 'sonner';
import i18n from '@/i18n';
import { Button } from '@/components/ui/atoms/Button';
import { X } from 'lucide-react';

// Maximum file size in bytes (10MB)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Supported file extensions
const SUPPORTED_EXTENSIONS = [
  'txt', 'md', 'pdf', 'docx', 'xlsx', 'pptx', 'png', 'jpg', 'jpeg', 
  'wav', 'mp3', 'webm', 'xml', 'csv', 'html'
];

// CV-specific file extensions (more restrictive)
const CV_SUPPORTED_EXTENSIONS = ['pdf', 'docx', 'doc', 'txt'];

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to get file extension
const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

// Helper function to validate file type
const validateFileType = (file: File, allowedExtensions: string[]): boolean => {
  const extension = getFileExtension(file.name);
  if (!allowedExtensions.includes(extension)) {
    toast.error(i18n.t('common:file_upload.type_unsupported', {
      extension,
      formats: allowedExtensions.map((ext) => `.${ext}`).join(', '),
    }));
    return false;
  }
  return true;
};

// Helper function to validate file size
const validateFileSize = (file: File, maxSize: number = MAX_FILE_SIZE): boolean => {
  if (file.size > maxSize) {
    toast.error(i18n.t('common:file_upload.too_large', {
      name: file.name,
      maxSize: formatFileSize(maxSize),
    }));
    return false;
  }
  return true;
};

// Combined validation function
const validateFile = (file: File, allowedExtensions: string[], maxSize: number = MAX_FILE_SIZE): boolean => {
  return validateFileType(file, allowedExtensions) && validateFileSize(file, maxSize);
};

export interface FileUploadProps {
  onFilesSelected: (files: File[]) => void;
  onFileRemove?: (index: number) => void;
  selectedFiles?: File[];
  acceptedFormats?: string[];
  maxFileSize?: number;
  multiple?: boolean;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
  placeholder?: string;
  description?: string;
  buttonText?: string;
  /** Hint shown below description in side-by-side variant (pass translated text from caller). */
  dragHint?: string;
  /** Heading above file list when showFileList is true (pass translated text from caller). */
  selectedFilesTitle?: string;
  id?: string;
  showFileList?: boolean;
  variant?: 'default' | 'side-by-side';
  iconColor?: string;
  buttonColor?: string;
  buttonTextColor?: string;
  /** Optional GTM tracking attributes for the browse (click to select files) action */
  browseTrackingAttributes?: Record<string, string>;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  onFilesSelected,
  onFileRemove,
  selectedFiles = [],
  acceptedFormats = SUPPORTED_EXTENSIONS,
  maxFileSize = MAX_FILE_SIZE,
  multiple = true,
  disabled = false,
  className = '',
  children,
  placeholder = 'Drag and drop files here, or click to browse',
  description,
  buttonText = 'Select Files',
  dragHint,
  selectedFilesTitle,
  id = 'file-upload',
  showFileList = true,
  variant = 'default',
  iconColor = 'text-dark-invirtus',
  buttonColor = 'bg-purple hover:bg-purple/90',
  buttonTextColor = 'text-dark-invirtus',
  browseTrackingAttributes
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const filesArray = Array.from(e.dataTransfer.files);
      const validFiles = filesArray.filter(file => validateFile(file, acceptedFormats, maxFileSize));
      onFilesSelected(validFiles);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const filesArray = Array.from(e.target.files);
      const validFiles = filesArray.filter(file => validateFile(file, acceptedFormats, maxFileSize));
      onFilesSelected(validFiles);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getAcceptAttribute = (): string => {
    return acceptedFormats.map(ext => `.${ext}`).join(',');
  };

  const renderDropzoneContent = () => {
    if (children) return children;

    if (variant === 'side-by-side') {
      return (
        <>
          <svg xmlns="http://www.w3.org/2000/svg" className={`h-12 w-12 mx-auto ${iconColor} mb-3`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          <label
            htmlFor={id}
            className={`block max-w-xs mx-auto w-full px-3 py-2.5 ${buttonColor} ${buttonTextColor} rounded-full cursor-pointer font-heading transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            {...browseTrackingAttributes}
          >
            {buttonText}
          </label>
          {description && (
            <p className="text-gray-500 text-xs mt-2 font-body">{description}</p>
          )}
          {dragHint && (
            <p className="text-gray-400 text-xs mt-1 font-body">{dragHint}</p>
          )}
        </>
      );
    }

    // Default variant
    return (
      <>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-10 w-10 mx-auto ${iconColor} mb-2`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        <p className="text-sm text-dark-invirtus mb-2 font-body">{placeholder}</p>
        {description && (
          <p className="text-xs text-gray-600 text-sm mb-4 font-body">{description}</p>
        )}
        <Button
          type="contained"
          intent="primary"
          label={buttonText}
          onClick={handleClick}
          disabled={disabled}
          {...browseTrackingAttributes}
        />
      </>
    );
  };

  const getDropzoneClasses = () => {
    const baseClasses = "border-2 border-dashed rounded-lg p-4 text-center bg-white";
    if (variant === 'side-by-side') {
      return `${baseClasses} border-gray-200`;
    }
    return `${baseClasses} border-dark-invirtus/30`;
  };

  return (
    <div className={className}>
      <div
        className={getDropzoneClasses()}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {renderDropzoneContent()}

        <input
          type="file"
          className="hidden"
          id={id}
          multiple={multiple}
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept={getAcceptAttribute()}
          disabled={disabled}
        />
      </div>

      {/* File List UI */}
      {showFileList && selectedFiles.length > 0 && (
        <div className="mt-4">
          {selectedFilesTitle && <h6 className="text-base">{selectedFilesTitle}</h6>}
          <ul className="divide-y divide-gray-200 max-h-40 overflow-y-auto overflow-x-hidden">
            {selectedFiles.map((file, index) => (
              <li key={index} className="py-2 flex justify-between items-center">
                <div className="flex items-center min-w-0 flex-1">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-purple mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-dark-invirtus font-body truncate">{file.name}</span>
                    <p className="text-xs text-gray-600 font-body">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                {onFileRemove && (
                  <Button
                    type="icon"
                    intent="danger"
                    size="sm"
                    onClick={() => onFileRemove(index)}
                    id={`remove-file-${index}`}
                    disabled={disabled}
                    aria-label="Remove file"
                    icon={<X />}
                    className="shrink-0"
                  />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// Export constants for use in other components
export { MAX_FILE_SIZE, SUPPORTED_EXTENSIONS, CV_SUPPORTED_EXTENSIONS, formatFileSize, validateFile };
